/**
 * Nearest scrollable ancestor of a node — the app scrolls inside `<main>`'s padded box
 * (`Layout`), not the window, so anything reacting to scroll has to find that box first.
 *
 * ## Why this is shared rather than written again
 *
 * It had been hand-rolled FOUR times before this file existed — `StickyFormHeader`,
 * `useSectionScrollSpy`, `ProductEditPage`, and the Analytics sticky band that prompted the
 * extraction — which is well past §36's "one hand-roll is allowed, the second is a shared
 * component". The three pre-existing copies are deliberately left alone here rather than
 * migrated in the same change; converging them is a separate, mechanical pass.
 *
 * ## ⚠️ It deliberately does NOT require `scrollHeight > clientHeight`
 *
 * `StickyFormHeader`'s docblock records what that check cost: it ran in an effect on first
 * render, when a data-driven page is still showing its skeleton and nothing overflows yet,
 * so it returned null, no scroll listener was ever attached, and the stuck state could
 * never fire. The header stayed sticky — that part is pure CSS — while its border never
 * appeared, which reads as a styling bug and is actually a missing subscription. Analytics
 * is data-driven too, so the same trap applies there.
 *
 * The size test is the wrong question anyway: `overflow: auto` marks where scrolling WILL
 * happen, whether or not the content overflows at this instant.
 */
export const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let el = node?.parentElement ?? null
  while (el) {
    const oy = getComputedStyle(el).overflowY
    if (oy === 'auto' || oy === 'scroll') return el
    el = el.parentElement
  }
  return null
}
